import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useContext
} from "react";
import {
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  Badge,
  CircularProgress
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import LabeledAutocomplete from "js/components/Shared/Form/Fields/LabeledAutocomplete";
import { FileUploader } from "react-drag-drop-files";
import { SwapHoriz } from "@material-ui/icons";
import IconButton from "@material-ui/core/IconButton";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import { useGetTypes, useGetSchema } from "./hooks";
import { FactoryAdapter, SuggestionsFactoryMethod } from "./helpers";
import { SquareFoot } from "../../../utils";
import { RulesDialog } from "./components/RulesDialog";
import { importWizard as importWizardMutation } from "../../../graphql/resolvers/importWizard.resolver";
import { NotificationContext } from "../../../notification-context";
import { CenteredCircularProgress } from "./components/CenteredCircularProgress";

const fileTypes = ["CSV", "TXT", "XLS", "XLSX"];

/* ---------- Helpers ------------------------------------------------------- */
const readFile = async (file, delimiterOverride) => {
  const ext = file.name
    .split(".")
    .pop()
    .toLowerCase();
  const parser = FactoryAdapter[ext];
  if (!parser) {
    throw new Error("Formato no soportado. Usa csv, txt, xls o xlsx.");
  }
  const result = await parser.parse({ file, delimiterOverride });
  return result;
};

const TightBadge = withStyles({
  badge: {
    top: 0, // igual que en DevTools
    right: 5, // igual que en DevTools
    // opcional: si querés que no se corra por transformaciones:
    transform: "scale(0.8)"
  }
})(Badge);

const applyRules = (value, rules) => {
  if (!rules || !rules.length) return value;
  const asString = value == null ? "" : String(value);
  for (var i = 0; i < rules.length; i++) {
    var r = rules[i];
    var entradaArr = r && r.entrada ? r.entrada : [];
    var match =
      (r.type === "in" && entradaArr.indexOf(asString) !== -1) ||
      (r.type === "notIn" && entradaArr.indexOf(asString) === -1) ||
      (r.type === "equal" && r.entrada === asString) ||
      (r.type === "regexp" && new RegExp(r.entrada).test(asString));
    if (match) return r.salida;
  }
  var def = rules.filter(function(r) {
    return r.type === "default";
  })[0];
  return def ? def.salida : value;
};

/* ---------- Component ----------------------------------------------------- */
export const ImportWizard = function ImportWizard(props) {
  /* ---------- Destructure props sin optional chaining ---------- */
  var client = props.client;
  var models = props.models || [];
  var ops = props.ops || [];
  var rules = props.rules || [];
  var match = props.match;
  var id = match && match.params ? match.params.id : undefined;

  /* ---------- Context ----------------------------------------- */
  var notificationCtx = useContext(NotificationContext);
  var openNotification =
    notificationCtx && notificationCtx.openNotification
      ? notificationCtx.openNotification
      : function() {};

  /* ---------- State ----------------------------------------------------- */
  const [file, setFile] = useState(null);
  const [delimiterOverride, setDelimiterOverride] = useState("");
  const [detectedDelimiter, setDetectedDelimiter] = useState("");
  const [headers, setHeaders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [correlations, setCorrelations] = useState([{ header: "", field: "" }]);
  const [originalCorrelations, setOriginalCorrelations] = useState([]);
  const [correlationSelected, setCorrelationSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingFile, setLoadingFile] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [allowDuplicatedEmails, setAllowDuplicatedEmails] = useState(false);
  const [otherOptions, setOtherOptions] = useState({ enableCron: false });

  /* ---------- Derived values --------------------------------------------- */
  const inBackOffice = window.location.pathname.indexOf("admin") !== -1;
  const idPrograma = inBackOffice
    ? parseInt(id, 10)
    : parseInt(localStorage.getItem("idPrograma"), 10);

  const results = useGetTypes({
    client: client,
    models: models,
    ops: ops,
    idPrograma: idPrograma
  });

  var suggestionsConstructor = null;
  var suggestions = [];
  if (models && results) {
    suggestionsConstructor = SuggestionsFactoryMethod.createSuggestions({
      models: models,
      results: results
    });
    suggestions = suggestionsConstructor.getSuggestions();
  }

  var getSchemaResp = useGetSchema({
    client: client,
    variables: { idPrograma: idPrograma }
  });

  var lastSchemaSaved =
    getSchemaResp && getSchemaResp.schema ? getSchemaResp.schema : undefined;
  var lastAllowDuplicatedEmails =
    getSchemaResp && typeof getSchemaResp.allowDuplicatedEmails !== "undefined"
      ? getSchemaResp.allowDuplicatedEmails
      : false;
  let lastOtherOptions =
    getSchemaResp && getSchemaResp.otherOptions
      ? getSchemaResp.otherOptions
      : undefined;

  /* ---------- Effect: load saved schema ----------------------------------- */
  useEffect(
    function() {
      if (lastSchemaSaved) {
        var parsed = JSON.parse(lastSchemaSaved);
        let parsedOtherOptions = JSON.parse(lastOtherOptions);
        setOriginalCorrelations(JSON.parse(JSON.stringify(parsed)));
        setCorrelations(parsed);
        setOtherOptions(parsedOtherOptions);
        setAllowDuplicatedEmails(lastAllowDuplicatedEmails);
      } else {
        setOriginalCorrelations([]);
        setCorrelations([{ header: "", field: "" }]);
      }
    },
    [lastSchemaSaved]
  );

  /* ---------- Utility: the full set of fields --------------------------- */
  const fieldsModel = [].concat(suggestions);

  /* ---------- Preview ----------------------------------------------------- */
  const preview = useMemo(
    function() {
      if (rows.length === 0) return [];
      return rows.map(function(row) {
        var obj = {};
        correlations.forEach(function(corr) {
          if (!corr.header || !corr.field) return;
          var headerLabel =
            corr.header && corr.header.label ? corr.header.label : "";
          var fieldLabel =
            corr.field && corr.field.label ? corr.field.label : "";
          var v = row[headerLabel];
          obj[fieldLabel] = applyRules(v, corr.rules);
        });
        return obj;
      });
    },
    [rows, correlations]
  );

  /* ---------- Callback: update one correlation -------------------------- */
  const setCorrelation = useCallback(function(corr, index) {
    setCorrelations(function(prev) {
      return prev.map(function(c, i) {
        return i === index ? corr : c;
      });
    });
  }, []);

  /* ---------- File change ------------------------------------------------ */
  const handleChange = async function(fileUploaded) {
    setLoadingFile(true);
    setFile(fileUploaded);
    const parsed = await readFile(fileUploaded, delimiterOverride);
    const filas = parsed.rows;
    const hdrs = parsed.headers;
    const delimiter = parsed.delimiter;

    setHeaders(hdrs);
    setRows(filas.slice(0, 10));
    setDetectedDelimiter(delimiter || "");

    const newCorrelations = hdrs.map(function(header) {
      const headerObj = { label: header, value: header };
      const matchField = fieldsModel.find(function(f) {
        return (
          f.value &&
          String(f.value).toLowerCase() === String(header).toLowerCase()
        );
      });
      return { header: headerObj, field: matchField ? matchField : "" };
    });

    const existingHeaders = new Set(
      correlations
        .filter(function(c) {
          return c.header && c.header.value;
        })
        .map(function(c) {
          return c.header.value;
        })
    );

    const uniqueNew = newCorrelations.filter(function(c) {
      return !existingHeaders.has(c.header.value);
    });

    if (uniqueNew.length && originalCorrelations.length === 0) {
      setCorrelations(function(prev) {
        return prev.concat(uniqueNew);
      });

      if (
        correlations.length === 1 &&
        (!correlations[0].header || !correlations[0].header.value) &&
        uniqueNew.length
      ) {
        setCorrelations([].concat(uniqueNew, [{ header: "", field: "" }]));
      }
    }

    setLoadingFile(false);
  };

  /* ---------- Dialog handling ------------------------------------------- */
  const handleOpenDialog = useCallback(function(corr, index) {
    setCorrelationSelected({ index: index, selected: corr });
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(function() {
    setOpenDialog(false);
  }, []);

  /* ---------- Handler for allow duplicated emails ------------------------- */
  const handleAllowDuplicatedEmailsChange = useCallback(function(event) {
    setAllowDuplicatedEmails(event.target.checked);
  }, []);

  const handleChangeOtherOptions = useCallback(function(event) {
    setOtherOptions({
      ...otherOptions,
      [event.target.id]: event.target.checked
    });
  }, []);

  /* ---------- Submit ---------------------------------------------------- */
  const onSubmit = async function() {
    setLoadingAction(true);
    try {
      const input = {
        file: file,
        idPrograma: idPrograma,
        tipoImportacion: "USUARIO",
        json: JSON.stringify(correlations),
        allowDuplicatedEmails: allowDuplicatedEmails,
        otherOptions: JSON.stringify(otherOptions),
        idUsuarioCreador: parseInt(localStorage.getItem("idUsuario"), 10)
      };

      const result = await client.mutate({
        mutation: importWizardMutation,
        variables: { input: input }
      });
      if (result && result.data && result.data.importWizard) {
        openNotification(result.data.importWizard.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setLoadingAction(false);
    }
  };

  const onSubmitSchema = async function() {
    setLoadingAction(true);
    try {
      const input = {
        file: null,
        idPrograma: idPrograma,
        tipoImportacion: "USUARIO",
        json: JSON.stringify(correlations),
        allowDuplicatedEmails: allowDuplicatedEmails,
        otherOptions: JSON.stringify(otherOptions),
        idUsuarioCreador: parseInt(localStorage.getItem("idUsuario"), 10)
      };

      const result = await client.mutate({
        mutation: importWizardMutation,
        variables: { input: input }
      });
      if (result && result.data && result.data.importWizard) {
        openNotification(result.data.importWizard.message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setLoadingAction(false);
    }
  };

  /* ---------- Check if something changed --------------------------------- */
  const hasChanges = useMemo(
    function() {
      const correlationsChanged =
        JSON.stringify(correlations) !== JSON.stringify(originalCorrelations);

      const allowDuplicatedEmailsChanged =
        allowDuplicatedEmails !== lastAllowDuplicatedEmails;

      const otherOptionsChanged =
        JSON.stringify(otherOptions) !== lastOtherOptions;

      return (
        correlationsChanged ||
        allowDuplicatedEmailsChanged ||
        otherOptionsChanged
      );
    },
    [correlations, originalCorrelations, allowDuplicatedEmails, otherOptions]
  );

  /* ---------- Render ---------------------------------------------------- */
  return (
    <>
      <Typography variant="h5">Asistente de Importación</Typography>
      <Paper>
        <Grid container style={{ marginBottom: "1%" }}>
          <Grid item xs={12} md={6} style={{ padding: "1% 1% 1% 1%" }}>
            <FileUploader
              label={"Arrastrá o hacé clic para subir archivos"}
              uploadedLabel={file ? file.name : ""}
              handleChange={handleChange}
              name="file"
              types={fileTypes}
              maxSize={64}
            />
            <Typography>
              Máx 64MB • Delimitador detectado:{" "}
              <div
                component="span"
                style={{
                  fontFamily: "monospace",
                  backgroundColor: "#f5f5f5",
                  paddingLeft: 4,
                  paddingRight: 4,
                  borderRadius: 4,
                  display: "inline-block"
                }}
              >
                {detectedDelimiter ? JSON.stringify(detectedDelimiter) : "—"}
              </div>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} style={{ padding: "1% 1% 1% 1%" }}>
            <TextField
              label="Forzar delimitador (opcional)"
              placeholder="; , | \t"
              value={delimiterOverride}
              onChange={function(e) {
                setDelimiterOverride(e.target.value);
              }}
              variant="filled"
              style={{ width: "100%", height: "50%" }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={16}>
        <Grid
          container
          item
          xs={12}
          md={6}
          spacing={16}
          alignContent="flex-start"
        >
          {/* Bloque 1: Encabezados del CSV / Excel */}
          <Grid item xs={12}>
            {
              <Paper style={{ padding: "2%" }}>
                {!loadingFile ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Encabezados del CSV / Excel
                    </Typography>
                    {headers.length > 0 ? (
                      headers.map(header => (
                        <Chip
                          key={String(header)}
                          label={String(header)}
                          style={{ margin: 1.5 }}
                        />
                      ))
                    ) : (
                      <Typography style={{ opacity: 0.7 }}>
                        {"Subí un archivo para ver los encabezados"}
                      </Typography>
                    )}
                  </>
                ) : (
                  <CenteredCircularProgress />
                )}
              </Paper>
            }
          </Grid>

          {/* Bloque 2: Otras opciones */}
          <Grid item xs={12}>
            <Paper style={{ padding: "2%" }}>
              <Typography variant="h6" gutterBottom>
                Otras opciones
              </Typography>
              <Tooltip
                title="Al activar esta opción, se permiten los emails duplicados durante la importación. Al desactivarla, se desestiman los emails duplicados y los usuarios a crear o actualizar quedarán con el campo vacío para luego ser completado por el usuario"
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowDuplicatedEmails}
                      onChange={handleAllowDuplicatedEmailsChange}
                      color="primary"
                    />
                  }
                  label="Permitir correos duplicados"
                />
              </Tooltip>
              <Tooltip
                title="Al activar esta opción, se activará el cron de importación asociado."
                placement="top"
              >
                <FormControlLabel
                  control={
                    <Switch
                      id="enableCron"
                      checked={otherOptions.enableCron}
                      onChange={handleChangeOtherOptions}
                      color="primary"
                    />
                  }
                  label="Habilitar cron de importación"
                />
              </Tooltip>
            </Paper>
          </Grid>
        </Grid>

        <Grid container item xs={12} md={6} alignContent="flex-end">
          {/* Bloque 3: Campos del modelo */}
          <Grid item xs={12}>
            <Paper style={{ padding: "1%" }}>
              <Typography variant="h6" gutterBottom>
                {"Campos del modelo " +
                  models
                    .map(function(m) {
                      return m.label;
                    })
                    .join("/")}
              </Typography>

              <div style={{ maxHeight: 500, overflowY: "auto" }}>
                {correlations.map(function(corr, idx) {
                  let rulesCount = Array.isArray(corr.rules)
                    ? corr.rules.length
                    : 0;
                  let hasRules = rulesCount > 0;

                  return (
                    <Grid
                      container
                      key={idx}
                      spacing={2}
                      alignItems="center"
                      style={{
                        marginBottom: 16,
                        borderRadius: 4,
                        position: "relative"
                      }}
                    >
                      <Grid item xs={12} md={4}>
                        <LabeledAutocomplete
                          label="Encabezado"
                          isDisabled={false}
                          noOptionsMessage={function() {
                            return "No se encontraron encabezados";
                          }}
                          placeholder="Seleccione un encabezado"
                          suggestions={headers.map(function(header) {
                            return { label: header, value: header };
                          })}
                          value={corr.header}
                          onChange={function(value) {
                            setCorrelations(function(prev) {
                              var copy = prev.slice();
                              copy[idx].header = value;
                              return copy;
                            });
                          }}
                        />
                      </Grid>

                      <Grid
                        container
                        item
                        xs={12}
                        md={2}
                        justify="center"
                        alignContent="center"
                      >
                        <SwapHoriz opacity={0.7} />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <LabeledAutocomplete
                          label="Campo"
                          isDisabled={false}
                          noOptionsMessage={function() {
                            return "No se encontraron campos del modelo";
                          }}
                          placeholder="Seleccione un campo"
                          suggestions={fieldsModel}
                          value={corr.field}
                          onChange={function(value) {
                            setCorrelations(function(prev) {
                              var copy = prev.slice();
                              copy[idx].field = value;
                              return copy;
                            });
                          }}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={2}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Tooltip title="Eliminar correlación">
                          <IconButton
                            aria-label="Eliminar correlación"
                            onClick={function() {
                              setCorrelations(function(prev) {
                                return prev.filter(function(_, i) {
                                  return i !== idx;
                                });
                              });
                            }}
                            disabled={correlations.length === 1}
                          >
                            <RemoveCircleOutlineIcon color="error" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Agregar regla">
                          {hasRules ? (
                            <TightBadge badgeContent={rulesCount} color="error">
                              <IconButton
                                aria-label="Agregar regla"
                                onClick={function() {
                                  handleOpenDialog(corr, idx);
                                }}
                              >
                                <SquareFoot color="primary" />
                              </IconButton>
                            </TightBadge>
                          ) : (
                            <IconButton
                              aria-label="Agregar regla"
                              onClick={function() {
                                handleOpenDialog(corr, idx);
                              }}
                            >
                              <SquareFoot color="primary" />
                            </IconButton>
                          )}
                        </Tooltip>
                      </Grid>
                    </Grid>
                  );
                })}
              </div>

              <Button
                variant="outlined"
                color="primary"
                onClick={function() {
                  setCorrelations(
                    [].concat(correlations, [{ header: "", field: "" }])
                  );
                }}
              >
                AGREGAR CORRELACIÓN
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <Grid container style={{ marginTop: "1%" }} spacing={16}>
        <Grid item xs={12} style={{ padding: "0.5%" }}>
          <Paper style={{ padding: "1%" }}>
            {!loadingFile ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Vista previa (primeras 10 filas transformadas)
                </Typography>
                {preview.length > 0 ? (
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: "2% 0 0 0",
                      backgroundColor: "#f5f5f5",
                      padding: 2,
                      borderRadius: 1
                    }}
                  >
                    {JSON.stringify(preview, null, 2)}
                  </pre>
                ) : (
                  <Typography style={{ opacity: 0.7 }}>
                    Debes subir un archivo para ver la vista previa…
                  </Typography>
                )}
              </>
            ) : (
              <CenteredCircularProgress />
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={16}
        style={{ marginTop: "1%", padding: "0.4%" }}
        justify="flex-end"
      >
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmitSchema}
            disabled={
              loadingFile ||
              loadingAction ||
              !hasChanges ||
              (correlations.length === 1 &&
                (!correlations[0].header || !correlations[0].field))
            }
            startIcon={loadingAction ? <CircularProgress size={18} /> : null}
            size="large"
          >
            GUARDAR ESQUEMA
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={loadingFile || loadingAction || preview.length === 0}
            startIcon={loadingAction ? <CircularProgress size={18} /> : null}
            size="large"
          >
            IMPORTAR
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            onClick={function() {
              setCorrelations([{ header: "", field: "" }]);
              setFile(null);
              setRows([]);
              setHeaders([]);
              setDetectedDelimiter("");
              setDelimiterOverride("");
              setOriginalCorrelations([]);
            }}
            size="large"
          >
            REINICIAR
          </Button>
        </Grid>
      </Grid>

      <RulesDialog
        idPrograma={idPrograma}
        client={client}
        open={openDialog}
        onClose={handleCloseDialog}
        correlation={correlationSelected}
        onSetCorrelation={setCorrelation}
        rulesOps={rules}
      />
    </>
  );
};
